import charmapJson from "../generated/charmap.json" with { type: "json" };
import type { CharmapFile } from "./types.js";

/** Gen 1 text codec (§4.5). Raw bytes are the identity; decoded strings are for display only. */

export const TEXT_TERMINATOR = 0x50;

const charmap = charmapJson as CharmapFile;

const decodeTable: (string | undefined)[] = new Array(256);
for (const [b, s] of Object.entries(charmap.decode)) decodeTable[Number(b)] = s;

const encodeTokens: [string, number][] = Object.entries(charmap.encode)
  .filter(([t]) => t.length > 0 && t !== "@")
  .sort((a, b) => b[0].length - a[0].length);

export class TextEncodeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TextEncodeError";
  }
}

/** Decodes up to the 0x50 terminator. Unknown bytes become U+FFFD. */
export function decodeText(b: Uint8Array): string {
  let s = "";
  for (const byte of b) {
    if (byte === TEXT_TERMINATOR) break;
    s += decodeTable[byte] ?? "\u{FFFD}";
  }
  return s;
}

/** Encodes to game bytes without terminator. Throws TextEncodeError on unmapped characters. */
export function encodeTextBytes(s: string): number[] {
  const out: number[] = [];
  let i = 0;
  outer: while (i < s.length) {
    for (const [tok, byte] of encodeTokens) {
      if (s.startsWith(tok, i)) {
        out.push(byte);
        i += tok.length;
        continue outer;
      }
    }
    throw new TextEncodeError(`character ${JSON.stringify(s[i])} has no Gen 1 encoding`);
  }
  return out;
}

/**
 * Encodes into a fixed-size field of `len` bytes: the text, a 0x50 terminator, then 0x50 padding.
 * Throws if the text plus terminator does not fit.
 */
export function encodeText(s: string, len: number): Uint8Array {
  const bytes = encodeTextBytes(s);
  if (bytes.length + 1 > len) throw new TextEncodeError(`"${s}" needs ${bytes.length + 1} bytes, field has ${len}`);
  const out = new Uint8Array(len).fill(TEXT_TERMINATOR);
  out.set(bytes);
  return out;
}

export const text: { encode(s: string, len: number): Uint8Array; decode(b: Uint8Array): string } = {
  encode: encodeText,
  decode: decodeText,
};

/** Every byte with a printable decoding (for round-trip tests and UI pickers). */
export function printableBytes(): number[] {
  const out: number[] = [];
  for (let b = 0; b < 256; b++) {
    const s = decodeTable[b];
    if (s !== undefined && b !== TEXT_TERMINATOR && b >= 0x60) out.push(b);
  }
  return out;
}
